// Workspace-wide Files browser. Server-side filters drive everything:
// extension-group chips, availability tabs, debounced filename search.
// Grid layout for image-heavy queries, list otherwise.

const FILES_TYPE_GROUPS = [
  { key: 'all',    label: 'All',          exts: null },
  { key: 'pdf',    label: 'PDFs',         exts: ['pdf'] },
  { key: 'sheet',  label: 'Spreadsheets', exts: ['xlsx','xls','csv'] },
  { key: 'doc',    label: 'Docs',         exts: ['doc','docx','txt','md'] },
  { key: 'slide',  label: 'Slides',       exts: ['ppt','pptx','key'] },
  { key: 'image',  label: 'Images',       exts: ['jpg','jpeg','png','gif','webp','heic','bmp'] },
  { key: 'video',  label: 'Videos',       exts: ['mp4','mov','avi','webm'] },
  { key: 'archive',label: 'Archives',     exts: ['zip','tar','gz','7z','rar'] },
];

const FilesView = ({ setView, setDealId }) => {
  const [items,      setItems]      = React.useState(null);
  const [total,      setTotal]      = React.useState(0);
  const [typeGroup,  setTypeGroup]  = React.useState('all');
  // available | all. Dropped the "Missing" pill — the back-fill script on
  // 2026-05-13 cleared every referenced_only row by direct download from
  // DealCloud, and the daemon now keeps the gap closed. Add it back if the
  // count ever spikes.
  const [status,     setStatus]     = React.useState('available');
  // all | unowned — "No owner" = DealCloud files attached to no Atlas note.
  const [scope,      setScope]      = React.useState('all');
  const [layout,     setLayout]     = React.useState('list');      // list | grid
  const [q,          setQ]          = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  // Cursor pagination — same shape as deals/companies. Single PAGE_SIZE
  // for offset-math safety, `loadedAll` hard-stops on empty responses.
  const [page, setPage] = React.useState(1);
  const [paging, setPaging] = React.useState(false);
  const [loadedAll, setLoadedAll] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 120;

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const buildParams = React.useCallback(() => {
    const group = FILES_TYPE_GROUPS.find((g) => g.key === typeGroup);
    const params = {};
    if (status === 'available') params.storage_status = 'available';
    if (scope === 'unowned') params.unowned = true;
    if (debouncedQ) params.q = debouncedQ;
    // Single-ext groups → server filter. Multi-ext groups (sheet, doc, slide,
    // image, video, archive) get filtered client-side after fetch. Caveat:
    // multi-ext filters reduce what's visible per page, so the sentinel
    // (positioned at the bottom of the rendered list) keeps triggering until
    // we've loaded enough server pages to fill the screen — that's the
    // intended behavior.
    if (group?.exts?.length === 1) params.file_extension = group.exts[0];
    return params;
  }, [typeGroup, status, scope, debouncedQ]);

  const fetchPage = React.useCallback(async (p, append) => {
    setPaging(true);
    try {
      const res = await window.AtlasApi.listAttachments({
        ...buildParams(), page: p, limit: PAGE_SIZE,
      });
      let rows = (res && res.items) || [];
      const group = FILES_TYPE_GROUPS.find((g) => g.key === typeGroup);
      if (group?.exts && group.exts.length > 1) {
        const set = new Set(group.exts);
        rows = rows.filter((a) => set.has((a.file_extension || '').toLowerCase()));
      }
      const newTotal = res?.total ?? rows.length;
      // Important: the empty-page check uses the SERVER's row count, not
      // the post-filter rows length. Client-side ext filter can produce
      // zero rows even though the server returned data — we want to keep
      // loading further pages in that case.
      const serverReturned = (res?.items || []).length;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        if (serverReturned === 0 || (append ? (prev?.length || 0) + serverReturned : serverReturned) >= newTotal) {
          setLoadedAll(true);
        }
        return next;
      });
      setTotal(newTotal);
      setPage(p);
    } catch {
      if (!append) { setItems([]); setTotal(0); setLoadedAll(true); }
    } finally {
      setPaging(false);
    }
  }, [buildParams, typeGroup]);

  const reload = React.useCallback(() => {
    setItems(null);
    setPage(1);
    setLoadedAll(false);
    return fetchPage(1, false);
  }, [fetchPage]);

  React.useEffect(() => { reload(); }, [reload]);

  // IntersectionObserver — auto-load next page within 240px of viewport.
  const hasMore = items != null && !loadedAll;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) {
        fetchPage(page + 1, true);
      }
    }, { rootMargin: '240px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  // Default to grid layout when browsing images.
  React.useEffect(() => {
    if (typeGroup === 'image' && layout !== 'grid') setLayout('grid');
  }, [typeGroup]);

  const rows = items || [];

  // Whole-view export (metadata, not the files themselves). Refetches the
  // current filters in one call when the pager hasn't finished; the menu
  // names the server cap when it would truncate.
  const EXPORT_CAP = 10000;
  const doExport = React.useCallback(async (format) => {
    let data;
    if (loadedAll) {
      data = rows;
    } else {
      const res = await window.AtlasApi.listAttachments({ ...buildParams(), page: 1, limit: EXPORT_CAP });
      data = (res && res.items) || [];
      const group = FILES_TYPE_GROUPS.find((g) => g.key === typeGroup);
      if (group?.exts && group.exts.length > 1) {
        const set = new Set(group.exts);
        data = data.filter((a) => set.has((a.file_extension || '').toLowerCase()));
      }
    }
    const cols = [
      { key: 'filename',   label: 'Filename' },
      { key: 'extension',  label: 'Type' },
      { key: 'size_bytes', label: 'Size (bytes)' },
      { key: 'company',    label: 'Company' },
      { key: 'engagement', label: 'Engagement' },
      { key: 'note_title', label: 'Note' },
      { key: 'source',     label: 'Source' },
      { key: 'status',     label: 'Storage status' },
      { key: 'created',    label: 'Created' },
      { key: 'id',         label: 'Atlas id' },
    ];
    const records = data.map((a) => ({
      filename: a.original_filename || '', extension: a.file_extension || '',
      size_bytes: a.size_bytes ?? '', company: a.company_name || '',
      engagement: a.engagement_name || '', note_title: a.note_title || '',
      source: a.source || '', status: a.storage_status || '',
      created: a.dc_created_at || a.created_at || '', id: a.id,
    }));
    const name = `files-${scope === 'unowned' ? 'no-owner-' : ''}${typeGroup}-${window.localISODate()}`;
    if (format === 'csv') window.exportCsv(`${name}.csv`, cols, records);
    else window.exportJson(`${name}.json`, records);
  }, [loadedAll, rows, buildParams, typeGroup, scope]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Files']}
        title="Files"
        meta={items === null ? 'Loading…' : `${rows.length.toLocaleString()} of ${total.toLocaleString()} files`}
        actions={(
          <>
          <window.ExportMenu
            disabled={items === null || !rows.length}
            viewCount={loadedAll ? rows.length : total}
            capNote={!loadedAll && total > EXPORT_CAP
              ? `Caps at the first ${EXPORT_CAP.toLocaleString()} files — narrow the filters for a complete file.`
              : null}
            onExport={doExport}
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search filenames…"
            style={{
              width: 240, padding: '4px 10px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 6, height: 28, fontFamily: 'inherit',
            }}
          />
          </>
        )}
      />

      {/* Type-group chips + availability tabs + layout switcher */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '0 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36,
      }}>
        {FILES_TYPE_GROUPS.map(({ key, label }) => {
          const active = typeGroup === key;
          return (
            <button key={key} onClick={() => setTypeGroup(key)} style={{
              padding: '0 8px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1,
            }}>{label}</button>
          );
        })}

        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {[['all','All files'],['unowned','No owner']].map(([k, label]) => (
              <button key={k} onClick={() => setScope(k)} style={{
                padding: '3px 9px', border: 'none', background: scope === k ? '#fff' : 'transparent',
                boxShadow: scope === k ? 'var(--shadow-sm)' : 'none',
                borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                color: scope === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer', height: 22,
              }}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {[['available','Available'],['all','All']].map(([k, label]) => (
              <button key={k} onClick={() => setStatus(k)} style={{
                padding: '3px 9px', border: 'none', background: status === k ? '#fff' : 'transparent',
                boxShadow: status === k ? 'var(--shadow-sm)' : 'none',
                borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                color: status === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer', height: 22,
              }}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7 }}>
            {[['list', <Icon.List size={12} />, 'List'], ['grid', <Icon.Kanban size={12} />, 'Grid']].map(([k, ic, label]) => (
              <button key={k} onClick={() => setLayout(k)} style={{
                padding: '3px 9px', border: 'none', background: layout === k ? '#fff' : 'transparent',
                boxShadow: layout === k ? 'var(--shadow-sm)' : 'none',
                borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                color: layout === k ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 5, height: 22,
              }}>
                {ic}{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--surface-2)' }}>
        {items === null && <SkeletonRows rows={6} variant="generic" />}
        {items && rows.length === 0 && (
          <div style={{
            background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10,
            padding: '32px 24px', maxWidth: 640, fontSize: 12.5, color: 'var(--text-3)',
          }}>
            No files match this filter.
          </div>
        )}

        {items && rows.length > 0 && layout === 'list' && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {rows.map((a, i) => (
              <window.FilesViewRow key={a.id} att={a} isFirst={i === 0} setDealId={setDealId} />
            ))}
          </div>
        )}

        {items && rows.length > 0 && layout === 'grid' && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12,
          }}>
            {rows.map((a) => <window.FilesViewCard key={a.id} att={a} setDealId={setDealId} />)}
          </div>
        )}

        {/* Sentinel sits below either layout so the same observer covers both. */}
        {hasMore && items !== null && (
          <div ref={sentinelRef} style={{ height: 1, marginTop: 8 }} />
        )}
        {paging && items !== null && rows.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <SkeletonRows rows={3} variant="generic" />
          </div>
        )}
        {!hasMore && items !== null && rows.length > 0 && (
          <div style={{
            marginTop: 12, padding: '10px 14px', textAlign: 'center', fontSize: 11.5,
            color: 'var(--text-4)',
          }}>
            End of list · {rows.length.toLocaleString()} {rows.length === 1 ? 'file' : 'files'}
          </div>
        )}
      </div>
    </div>
  );
};

// Linkage chip — clickable when there's an engagement to jump to, dim text
// when only a company is known (no companies view to deep-link to yet).
// `more` is the +N suffix shown when a single file is attached to multiple
// notes (e.g. an NDA template that's been linked into 80 deals).
const LinkageChip = ({ att, setDealId }) => {
  const more = att.link_count > 1 ? att.link_count - 1 : 0;
  // Stop propagation so the surrounding <a> (file download) doesn't trigger
  // when the chip itself is clicked.
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const chipStyle = {
    fontSize: 10.5, padding: '1px 6px', borderRadius: 4, fontWeight: 500,
    background: 'var(--surface-2)', color: 'var(--text-3)',
    border: '1px solid var(--border)', cursor: 'pointer', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
  };

  if (att.engagement_id) {
    return (
      <button onClick={(e) => { stop(e); setDealId(att.engagement_id); }}
        style={{ ...chipStyle, border: '1px solid var(--border)' }}
        title={att.note_title || ''}>
        <Icon.Pipeline size={9} /> {att.engagement_name || 'Engagement'}
        {more > 0 && <span style={{ opacity: 0.55, marginLeft: 2 }}>+{more}</span>}
      </button>
    );
  }
  if (att.company_id) {
    return (
      <span style={{ ...chipStyle, cursor: 'default' }} title={att.note_title || ''}>
        <Icon.Building size={9} /> {att.company_name || 'Company'}
        {more > 0 && <span style={{ opacity: 0.55, marginLeft: 2 }}>+{more}</span>}
      </span>
    );
  }
  return null;  // Truly orphaned — don't add visual noise
};

// Row layout — same row component used in the right-rail FilesPanel, but
// promoted to full-width.
const FilesViewRow = ({ att, isFirst, setDealId }) => {
  const available = att.storage_status === 'available' && att.storage_uri;
  const hue = (window.ATTACHMENT_EXT_HUE && window.ATTACHMENT_EXT_HUE[(att.file_extension || '').toLowerCase()]) || 'slate';
  const h = HUE[hue] || HUE.slate;
  const sizeStr = att.size_bytes != null ? (
    att.size_bytes < 1024 ? `${att.size_bytes} B` :
    att.size_bytes < 1024 * 1024 ? `${(att.size_bytes / 1024).toFixed(0)} KB` :
    `${(att.size_bytes / 1024 / 1024).toFixed(1)} MB`
  ) : '';
  const dateStr = att.dc_created_at
    ? new Date(att.dc_created_at).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
    : (att.created_at ? new Date(att.created_at).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : null);
  const _IMG = new Set(['jpg','jpeg','png','gif','webp','svg','bmp']);
  const isImg = available && _IMG.has((att.file_extension || '').toLowerCase());
  const Tag = available ? 'a' : 'div';
  // Downloads sit behind the login wall — a bare href can't carry the auth
  // header, so clicks resolve a fresh signed URL through the API client.
  const tagProps = available
    ? { href: '#', onClick: (e) => { e.preventDefault(); window.AtlasApi.openAttachment(att.id, att.original_filename); } }
    : {};

  return (
    <Tag {...tagProps}
      title={!available ? 'Referenced in DealCloud but the file is no longer in the backup.'
        : window.AtlasApi.attachmentOpensInline(att.original_filename) ? 'Open in new tab' : 'Download'}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
        borderTop: isFirst ? 'none' : '1px solid var(--border)',
        cursor: available ? 'pointer' : 'default', textDecoration: 'none', color: 'inherit',
        background: 'transparent',
      }}
      onMouseEnter={(e) => { if (available) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      {isImg ? (
        <window.AuthThumb attachmentId={att.id} alt={att.original_filename}
          style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'cover', flexShrink: 0, background: 'var(--surface-2)' }} />
      ) : (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 5, flexShrink: 0,
          background: available ? h.bg : 'var(--surface-2)',
          color:      available ? h.fg : 'var(--text-4)',
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>{(att.file_extension || '·').slice(0, 4)}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500,
          color: available ? 'var(--text)' : 'var(--text-3)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{att.original_filename}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
          {sizeStr && <span>{sizeStr}</span>}
          {dateStr && <><span>·</span><span>{dateStr}</span></>}
          {att.source === 'manual' && <><span>·</span><Pill hue="green" sz="sm">uploaded</Pill></>}
          {!available && <><span>·</span><Pill hue="slate" sz="sm">missing</Pill></>}
          {(att.engagement_id || att.company_id) && (
            <>
              <span>·</span>
              <LinkageChip att={att} setDealId={setDealId} />
            </>
          )}
        </div>
      </div>
      {available && <AttachmentTextButton att={att} />}
      {available && (window.AtlasApi.attachmentOpensInline(att.original_filename)
        ? <Icon.External size={12} color="var(--text-4)" />
        : <Icon.Download size={12} color="var(--text-4)" />)}
    </Tag>
  );
};

// "Text" — what the file says, from Firecrawl /parse (attachment_texts). A
// button inside the row's <a>, so it stops the click from starting a
// download. 404 means the parser has not been over this file yet.
const AttachmentTextButton = ({ att }) => {
  const [doc, setDoc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const open = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try {
      setDoc(await window.AtlasApi.attachmentText(att.id));
    } catch (err) {
      pushToast({ type: 'info', title: 'No text yet',
        body: /404/.test(err.message || '') ? 'This file has not been parsed yet.' : err.message });
    } finally { setBusy(false); }
  };
  const ext = (att.file_extension || '').toLowerCase();
  if (!['pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'csv', 'txt', 'rtf'].includes(ext)) return null;
  return (
    <>
      <button onClick={open} disabled={busy} title="Read the extracted text"
        style={{ fontSize: 11, padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)',
          background: 'var(--surface)', color: 'var(--text-3)', cursor: 'pointer', flexShrink: 0 }}>
        {busy ? '…' : 'Text'}
      </button>
      {doc && (
        <Drawer open={true} onClose={() => setDoc(null)} width={720}
          title={doc.filename}
          subtitle={doc.error ? `Could not parse: ${doc.error}`
            : `${doc.page_count ? `${doc.page_count} page(s) · ` : ''}${(doc.char_count || 0).toLocaleString()} characters · parsed ${doc.parsed_at ? new Date(doc.parsed_at).toLocaleDateString() : ''}`}>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.55,
            color: 'var(--text-2)', margin: 0 }}>{doc.text || '(no text)'}</pre>
        </Drawer>
      )}
    </>
  );
};

// Grid card — square thumbnail layout, dominant for image-heavy browsing.
const FilesViewCard = ({ att, setDealId }) => {
  const available = att.storage_status === 'available' && att.storage_uri;
  const hue = (window.ATTACHMENT_EXT_HUE && window.ATTACHMENT_EXT_HUE[(att.file_extension || '').toLowerCase()]) || 'slate';
  const h = HUE[hue] || HUE.slate;
  const _IMG = new Set(['jpg','jpeg','png','gif','webp','svg','bmp']);
  const isImg = available && _IMG.has((att.file_extension || '').toLowerCase());
  const Tag = available ? 'a' : 'div';
  const tagProps = available
    ? { href: '#', onClick: (e) => { e.preventDefault(); window.AtlasApi.openAttachment(att.id, att.original_filename); } }
    : {};

  return (
    <Tag {...tagProps}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
        overflow: 'hidden', textDecoration: 'none', color: 'inherit', display: 'block',
        cursor: available ? 'pointer' : 'default',
        transition: 'border-color .12s, box-shadow .12s',
      }}
      onMouseEnter={(e) => { if (available) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; } }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{
        height: 110, background: isImg ? '#000' : (available ? h.bg : 'var(--surface-2)'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: available ? h.fg : 'var(--text-4)',
      }}>
        {isImg ? (
          <window.AuthThumb attachmentId={att.id} alt={att.original_filename}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <span style={{ fontSize: 22, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {(att.file_extension || '·').slice(0, 4)}
          </span>
        )}
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: available ? 'var(--text)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {att.original_filename}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {att.size_bytes != null && (
            <span>{att.size_bytes < 1024 * 1024 ? `${Math.round(att.size_bytes / 1024)} KB` : `${(att.size_bytes / 1024 / 1024).toFixed(1)} MB`}</span>
          )}
          {!available && <Pill hue="slate" sz="sm">missing</Pill>}
          {att.source === 'manual' && <Pill hue="green" sz="sm">uploaded</Pill>}
        </div>
        {(att.engagement_id || att.company_id) && (
          <div style={{ marginTop: 6 }}>
            <LinkageChip att={att} setDealId={setDealId} />
          </div>
        )}
      </div>
    </Tag>
  );
};

window.FilesView      = FilesView;
window.FilesViewRow   = FilesViewRow;
window.FilesViewCard  = FilesViewCard;
window.LinkageChip    = LinkageChip;
window.ATTACHMENT_EXT_HUE = window.ATTACHMENT_EXT_HUE || {
  pdf: 'rose', xls: 'green', xlsx: 'green', csv: 'green',
  doc: 'blue', docx: 'blue', txt: 'slate', md: 'slate',
  ppt: 'amber', pptx: 'amber',
  jpg: 'violet', jpeg: 'violet', png: 'violet', gif: 'violet', webp: 'violet', heic: 'violet',
  mp4: 'violet', mov: 'violet', avi: 'violet', webm: 'violet',
  zip: 'slate', tar: 'slate', gz: 'slate', '7z': 'slate', rar: 'slate',
};
